import React from 'react';
import { NextPage } from 'next';
import { getClientIp, getWindow, slugify, UserState } from '@surfline/web-common';

import { fetchOSData } from 'actions/deviceData';
import { fetchCompleteSpotData } from 'actions/spot';
import { fetchFavoriteSubregions, fetchForecasterContent } from 'actions/subregion';
import SpotPage from 'containers/SpotPage';
import { getUser } from 'common/api/user';
import { SL_WEB_LINEUP_CAM_PAYWALL } from 'common/treatments';
import type { AppPageProps } from 'pages/_app';
import { getOrCreateStore } from 'stores';
import { PermissionTypes } from 'types/permissions';
import type { WavetrakContext, WavetrakPageContext } from 'types/wavetrak';
import { forecastDaysAllowed } from 'utils/forecastDaysAllowed';
import getCloudflareCountryCode from 'utils/getCloudflareCountryCode';
import sendRedirect from 'utils/sendRedirect';
import { getServerTreatments } from 'utils/serverSplitClient';

const SpotReportPage: NextPage<{}, AppPageProps> = () => <SpotPage />;

SpotReportPage.getInitialProps = async (context: WavetrakPageContext) => {
  const store = getOrCreateStore();
  const { req, res, query } = context;
  const isServer = !!req;
  const cookies = req?.cookies || {};
  const { dispatch, getState } = store;
  const { spotSlug, spotId } = query as {
    spotSlug: string;
    spotId: string;
  };

  const clientIp = getClientIp(context.req) || '';
  const countryCode = getCloudflareCountryCode(req) || '';

  const userData = await getUser(context as WavetrakContext, isServer);

  const has16DayPermission = userData?.permissions?.some(
    (permission) => permission.name === PermissionTypes.CORE_16DAY_FORECAST,
  );

  const anonymousID = req?.cookies?.ajs_anonymous_id;

  const treatments =
    anonymousID && isServer
      ? await getServerTreatments([SL_WEB_LINEUP_CAM_PAYWALL], userData as UserState, anonymousID)
      : getWindow()?.__WAVETRAK_TREATMENTS__;

  const includeLineupCams = treatments?.[SL_WEB_LINEUP_CAM_PAYWALL] !== 'off';
  const includeInternalCams = !!userData?.details?.isInternal;

  const daysAllowed = forecastDaysAllowed({
    type: 'forecast-content',
    isEntitled: has16DayPermission,
    query,
  });

  // Use the centralized spot data fetching action
  await dispatch(
    fetchCompleteSpotData(
      spotId,
      cookies,
      clientIp,
      countryCode,
      (subregionId: string) => {
        // Server-side specific fetching for subregion data
        const subregionPromises = [];

        subregionPromises.push(dispatch(fetchFavoriteSubregions(cookies)));

        // Get the current state to check forecastStatus
        const currentState = getState();
        const spotReport = currentState.spot?.report?.data;

        if (spotReport?.spot?.subregion?.forecastStatus === 'active') {
          subregionPromises.push(
            dispatch(
              fetchForecasterContent({
                cookies,
                days: daysAllowed,
                subregionId,
              }),
            ),
          );
        }

        return Promise.all(subregionPromises);
      },
      includeLineupCams,
      includeInternalCams || undefined,
    ),
  );

  if (isServer) {
    const newrelic = require('newrelic');
    newrelic.addCustomAttributes({
      spotSlug,
      spotId,
    });

    await dispatch(fetchOSData(context.req!.headers));
    const state = getState();
    const spot = state.spot.report?.data?.spot;
    if (spot && spot._id !== spotId) {
      sendRedirect(res, `../${slugify(spot.name)}/${spot._id}`, 301);
      return {};
    }

    return {
      enableCssBaseline: true,
      enableTheme: true,
      isAppBannerEnabled: true,
      isSpotPage: true,
      ssrReduxState: state,
    };
  }

  return {
    enableCssBaseline: true,
    enableTheme: true,
    isAppBannerEnabled: true,
    isSpotPage: true,
  };
};

export default SpotReportPage;
